import './FileUploader.css';
import * as React from 'react';
import { Icon, PrimaryButton } from 'office-ui-fabric-react';
import { StorageInfo } from '../../../store/Tenant';
import { ConfirmationDialog } from '../../common/ConfirmationDialog';
import { formatBytes } from '../../utils/common';
import { ApplicationState } from '../../../store';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';

type OwnProps = {
    readonly?: boolean;
    blur?: boolean;
    children?: React.ReactNode;
    onFilePicked?: (files: File[]) => void;
    upoaderRef: React.RefObject<HTMLInputElement>;
}

type StateProps = {
    storage: StorageInfo;
}

type Props = OwnProps & StateProps & RouteComponentProps<{}>;

const FileUploaderImpl = React.forwardRef<HTMLInputElement, Props>(({ children, blur, onFilePicked, storage, history, readonly }, ref) => {
    const [isDragging, setIsDragging] = React.useState(false);
    const handleDragEnter = React.useCallback(() => setIsDragging(true), []);
    const handleDragLeave = React.useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();

        if (e.relatedTarget && e.currentTarget.contains(e.relatedTarget as any)) {
            return;
        }

        setIsDragging(false);
    }, []);

    const [files, setFiles] = React.useState<File[]>();

    const handleDragOver = React.useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
    }, []);

    const handleDrop = React.useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setFiles(Array.from(e.dataTransfer.files));
        setIsDragging(false);
    }, []);

    const [showFileSizeError, setShowFileSizeError] = React.useState(false);
    const [showCapacityError, setShowCapacityError] = React.useState(false);

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const targetFiles = e.target.files;
        if (!targetFiles) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();

        setFiles(Array.from(targetFiles));
    }

    React.useEffect(() => {
        if (!storage || !files?.length) {
            return;
        }

        setFiles(undefined);
        inputRef.current && (inputRef.current.value = '');

        const batchSize = files.reduce((a, b) => a + b.size, 0);
        if (batchSize >= storage.maxFileSize) {
            setShowFileSizeError(true);
            return;
        }

        if (storage.usage + batchSize > storage.capacity) {
            setShowCapacityError(true);
            return;
        }

        onFilePicked?.(files);
    }, [files, storage]);

    const handlers = readonly
        ? {}
        : {
            onDrop: handleDrop,
            onDragEnter: handleDragEnter,
            onDragLeave: handleDragLeave,
            onDragOver: handleDragOver
        }

    const inputRef = React.useRef<HTMLInputElement>(null)
    React.useImperativeHandle(ref, () => inputRef.current!, []);

    return <div
        className='file-uploader'
        {...handlers}>
        {
            readonly
                ? children
                : <><input ref={inputRef} type="file" onChange={onChange} multiple />
                    {
                        isDragging && <div className="file-uploader-drag">
                            <div className='file-uploader-background' style={{ visibility: blur ? "visible" : "hidden" }}>
                                {children}
                            </div>
                            <div className='file-uploader-buttons'>
                                <Icon iconName='PPMXFileUpload' />
                                <PrimaryButton text="Drag-n-drop to upload" />
                            </div>
                        </div>
                    }
                    {
                        !isDragging && children
                    }
                </>
        }
        {showFileSizeError && <ConfirmationDialog
            onDismiss={() => setShowFileSizeError(false)}
            onYes={() => setShowFileSizeError(false)}
            yesButtonProps={{ text: "Got it" }}
            dialogContentProps={dialogTitle}
        >
            The files you're trying to upload exceeds the {formatBytes(storage!.maxFileSize)} limit. Please upload large files to the external storage and add a link to them as an attachment instead.
        </ConfirmationDialog>}
        {showCapacityError && <ConfirmationDialog
            onDismiss={() => setShowCapacityError(false)}
            onYes={() => history.push("/billing")}
            yesButtonProps={{ text: "Upgrade plan", className: "upgrade-plan" }}
            dialogContentProps={dialogTitle}
        >
            The maximum storage capacity for Attachments allowed by your current subscription plan has been reached for this tenant. To continue uploading files, please contact us to either upgrade to a higher-tier plan or purchase additional storage.
        </ConfirmationDialog>}
    </div>
});

export const downloadFileByUrl = (url: string) => {
    const link = document.createElement("a");
    link.href = url;
    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);
}

const dialogTitle = { title: 'File Upload Limit Reached' };

const mapStateToProps = (state: ApplicationState) => ({
    storage: state.tenant.storage
})

export const FileUploader = withRouter<OwnProps>(connect(mapStateToProps)((props: Props) => <FileUploaderImpl ref={props.upoaderRef} {...props} />));